import http from '@/lib/http'
import { isUserTrialPrescriber, preloadManyItems } from '@/lib/permissions'
import { useFilterStore } from '@/lib/stores/filter'
import { useUtilityStore } from '@/lib/stores/utils'
import { familyKeyOf, orderFamilyKeyOf } from '@/lib/med-requests'
import type { MedRequest } from '@/types/data'
import { useQuery } from '@tanstack/react-query'
import { useFlag } from '@unleash/proxy-client-react'

export type GetMedRequestsResponse = {
  total: number
  data: Array<MedRequest>
}

/** One queue entry per family: opening any of its requests loads and decides the whole family. */
const dedupeFamilyMedRequests = (medRequests: Array<MedRequest>): Array<MedRequest> => {
  const seen = new Set<string>()

  return medRequests.filter(medRequest => {
    const key = familyKeyOf(medRequest)
    if (!key) return true

    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

/** Drops multi product orders; only siblings in this page are seen, the next-request prefetch catches the rest. */
const withoutMultiProductOrders = (medRequests: Array<MedRequest>): Array<MedRequest> => {
  const keys = medRequests.map(orderFamilyKeyOf)
  const counts = new Map<string, number>()
  for (const key of keys) {
    if (key) counts.set(key, (counts.get(key) ?? 0) + 1)
  }

  return medRequests.filter((_, index) => {
    const key = keys[index]
    return !key || counts.get(key) === 1
  })
}

const fetchFilteredMedRequests = async ({
  orderNumbers,
  shippingMethods,
  categories,
  excludedCategories,
  cutoffTime,
  priority,
  onHolds,
  isMultiProductOrdersEnabled
}: {
  orderNumbers: Array<string>
  shippingMethods: Array<string>
  categories?: Array<string>
  excludedCategories?: Array<string>
  cutoffTime?: 'before' | 'after'
  priority?: boolean
  onHolds?: boolean
  isMultiProductOrdersEnabled: boolean
}) => {
  const PAGE_SIZE = preloadManyItems() ? 50 : 5
  const params = new URLSearchParams()
  params.append('limit', PAGE_SIZE.toString())

  if (cutoffTime) {
    params.append('cutoffTime', cutoffTime)
  }

  // Joined values with comma.
  Object.entries({
    orderNumbers,
    shippingMethods,
    categories,
    excludedCategories
  }).forEach(([key, value]) => {
    if (value?.length) {
      params.append(key, value.join(','))
    }
  })

  Object.entries({
    priority,
    onHolds
  }).forEach(([key, value]) => {
    if (value) {
      params.append(key, 'true')
    }
  })

  const result = await http.get<GetMedRequestsResponse, GetMedRequestsResponse>('/prescriber/med-requests', {
    params,
    timeout: (http.defaults.timeout ?? 10000) * 6
  })

  // Order-number searches too: every item of a multi product order matches its number but opens as one page.
  const deduped = dedupeFamilyMedRequests(
    isMultiProductOrdersEnabled ? result.data : withoutMultiProductOrders(result.data)
  )
  // The API still counts siblings individually. Exact when this fetch holds the whole queue;
  // otherwise only the siblings dropped here can be subtracted.
  result.total =
    result.total === result.data.length ? deduped.length : result.total - (result.data.length - deduped.length)
  result.data = deduped

  if (isUserTrialPrescriber()) {
    const { actionedMedicationRequestIds } = useUtilityStore.getState()
    result.data = result.data.filter(medRequest => !actionedMedicationRequestIds.includes(medRequest.id))
  }

  return result
}

const useMedRequestFiltering = (enabled = true) => {
  const { filteringOptions, getCacheKey } = useFilterStore()

  // @ts-expect-error cutoffTime is a string
  const cutoffTime = filteringOptions['Cutoff Time']?.[0] as 'before' | 'after' | undefined

  const orderNumbers = filteringOptions['Order Number']
  const shippingMethods = filteringOptions['Shipping Method']
  const categories = filteringOptions.categories
  const excludedCategories = filteringOptions.excludedCategories
  const priority = filteringOptions.priority
  const onHolds = filteringOptions.onHolds
  const isMultiProductOrdersEnabled = useFlag('multi-product-orders')

  const query = useQuery<GetMedRequestsResponse>({
    queryKey: [...getCacheKey(), isMultiProductOrdersEnabled],
    queryFn: async () =>
      fetchFilteredMedRequests({
        orderNumbers,
        shippingMethods,
        categories,
        excludedCategories,
        cutoffTime,
        priority,
        onHolds,
        isMultiProductOrdersEnabled
      }),
    refetchInterval: 30000,
    enabled
  })

  return query
}

export default useMedRequestFiltering
